import React, { useState } from 'react'
import { Box, Chip, Collapse, IconButton, Paper, Stack, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

export interface AppDrawerSectionProps {
  icon?: React.ReactNode
  title?: React.ReactNode
  count?: number | string
  previewText?: string
  action?: React.ReactNode
  collapsible?: boolean
  defaultOpen?: boolean
  /**
   * Override the body padding. Defaults match the ExceptionDrawer
   * accordion (px 1.5, pb 1.25). The header occupies the top edge so
   * we drop pt to 0 when a header is present.
   */
  bodyPadding?: SxProps<Theme>
  children?: React.ReactNode
  sx?: SxProps<Theme>
}

const AppDrawerSection: React.FC<AppDrawerSectionProps> = ({
  icon,
  title,
  count,
  previewText,
  action,
  collapsible = false,
  defaultOpen = true,
  bodyPadding,
  children,
  sx,
}) => {
  const [open, setOpen] = useState(defaultOpen)
  const hasHeader = icon != null || title != null || action != null || count != null
  const expanded = collapsible ? open : true

  const bodyPaddingResolved =
    bodyPadding ??
    (hasHeader ? { px: 1.5, pt: 0, pb: 1.25 } : { px: 1.5, pt: 1.25, pb: 1.25 })

  const body = children != null && (
    <Box sx={bodyPaddingResolved as SxProps<Theme>}>{children}</Box>
  )

  return (
    <Paper
      variant="outlined"
      sx={[
        {
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {hasHeader && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            minHeight: 44,
            px: 1.5,
            py: 0.75,
            cursor: collapsible ? 'pointer' : 'default',
          }}
          onClick={collapsible ? () => setOpen(v => !v) : undefined}
          role={collapsible ? 'button' : undefined}
          aria-expanded={collapsible ? expanded : undefined}
          aria-label={
            collapsible
              ? expanded
                ? 'Collapse section'
                : 'Expand section'
              : undefined
          }
        >
          {icon != null && (
            <Box sx={{ display: 'flex', color: 'text.secondary', flexShrink: 0 }}>{icon}</Box>
          )}
          {title != null && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.85rem',
                lineHeight: 1.3,
                flexShrink: 0,
                color: 'text.primary',
              }}
            >
              {title}
            </Typography>
          )}
          {typeof count === 'number'
            ? count > 0 && (
                <Chip
                  label={count}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )
            : count != null && (
                <Chip
                  label={count}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
          {collapsible && !expanded && previewText && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                fontSize: '0.72rem',
                ml: 'auto',
                pl: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {previewText}
            </Typography>
          )}
          {action != null && (
            <Box
              sx={{ ml: previewText && !expanded ? 1 : 'auto', display: 'flex' }}
              onClick={e => e.stopPropagation()}
            >
              {action}
            </Box>
          )}
          {collapsible && (
            <IconButton
              size="small"
              tabIndex={-1}
              aria-hidden
              sx={{
                ml: action || (previewText && !expanded) ? 0 : 'auto',
                transition: 'transform 150ms ease',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )}
      {collapsible ? (
        <Collapse in={expanded} unmountOnExit>
          {body}
        </Collapse>
      ) : (
        body
      )}
    </Paper>
  )
}

export default AppDrawerSection
